import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  FiGrid,
  FiPlus,
  FiFileText,
  FiDownload,
  FiTag,
  FiLogOut,
  FiGlobe,
  FiUser,
  FiMail,
  FiUsers,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";
import styles from "./Sidebar.module.css";
import logo from "./primecloudsystems_logo_white.webp";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function Sidebar({
  onLinkClick,
  showCloseButton = false,
  onLogout,
  onCloseSidebar,
}) {
  const [user, setUser] = useState(readUser());

  useEffect(() => {
    const handleStorage = () => setUser(readUser());
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const can = (permission) => user && Number(user[permission]) === 1;

  const hasProductAccess =
    can("product_add") ||
    can("product_view") ||
    can("product_import") ||
    can("product_edit") ||
    can("product_delete");

  const hasCategoryAccess =
    can("category_add") ||
    can("category_view") ||
    can("category_edit") ||
    can("category_delete");

  const hasBrandAccess =
    can("brand_add") ||
    can("brand_view") ||
    can("brand_edit") ||
    can("brand_delete");

  const handleLogoutClick = () => {
    if (onLogout) onLogout();
  };

  const handleCloseSidebar = () => {
    if (onCloseSidebar) onCloseSidebar();
  };

  const handleVisitWebsite = () => {
    window.open("/", "_blank", "noopener");
    if (onLinkClick) onLinkClick();
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <img src={logo} alt="Logo" className={styles.logo} />
        {showCloseButton && (
          <button
            type="button"
            className={styles.closeSidebarButton}
            onClick={handleCloseSidebar}
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      <nav className={styles.nav}>
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `${styles.navLink} ${isActive ? styles.active : ""}`
          }
          onClick={onLinkClick}
        >
          <FiGrid className={styles.icon} /> Dashboard
        </NavLink>

        {hasProductAccess && (
          <div className={styles.navSection}>
            <div className={styles.sectionTitle}>Products</div>
            {can("product_add") && (
              <NavLink
                to="/product/add"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiPlus className={styles.icon} /> Add Product
              </NavLink>
            )}
            {can("product_view") && (
              <NavLink
                to="/product/view"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiFileText className={styles.icon} /> View Products
              </NavLink>
            )}
            {can("product_import") && (
              <NavLink
                to="/product/import"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiDownload className={styles.icon} /> Import Products
              </NavLink>
            )}
          </div>
        )}

        {hasCategoryAccess && (
          <div className={styles.navSection}>
            <div className={styles.sectionTitle}>Categories</div>
            {can("category_add") && (
              <NavLink
                to="/category/add"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiPlus className={styles.icon} /> Add Category
              </NavLink>
            )}
            {can("category_view") && (
              <NavLink
                to="/category/view"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiFileText className={styles.icon} /> View Categories
              </NavLink>
            )}
          </div>
        )}

        {hasBrandAccess && (
          <div className={styles.navSection}>
            <div className={styles.sectionTitle}>Brands</div>
            {can("brand_add") && (
              <NavLink
                to="/brand/add"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiPlus className={styles.icon} /> Add Brand
              </NavLink>
            )}
            {can("brand_view") && (
              <NavLink
                to="/brand/view"
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ""}`
                }
                onClick={onLinkClick}
              >
                <FiTag className={styles.icon} /> View Brands
              </NavLink>
            )}
          </div>
        )}

        <div className={styles.navSection}>
          <div className={styles.sectionTitle}>Account</div>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.active : ""}`
            }
            onClick={onLinkClick}
          >
            <FiUser className={styles.icon} /> Profile
          </NavLink>
          {can("contact_access") && (
            <NavLink
              to="/contact/messages"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={onLinkClick}
            >
              <FiMail className={styles.icon} /> Contact Messages
            </NavLink>
          )}
          {can("user_manage") && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={onLinkClick}
            >
              <FiUsers className={styles.icon} /> Users
            </NavLink>
          )}
          <NavLink
            to="/dashboard/data-detail"
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.active : ""}`
            }
            onClick={onLinkClick}
          >
            <FiTrendingUp className={styles.icon} /> Data Detail
          </NavLink>
        </div>
      </nav>

      <div className={styles.sidebarFooter}>
        <button className={styles.websiteButton} onClick={handleVisitWebsite}>
          <FiGlobe /> Visit Website
        </button>
        <button className={styles.logoutButton} onClick={handleLogoutClick}>
          <FiLogOut /> Logout
        </button>
      </div>
    </div>
  );
}
